import React, { useEffect, useState, useCallback } from 'react';
import { compose } from 'ramda';
import { RouteComponentProps } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import { showToast, NoData } from '@deeptrue-frontend/ui';
import api from 'api/api';
import { useFormatMessage } from 'components/IntlProvider';
import StoreProvider from 'components/StoreProvider/StoreProvider';
import { OrganizationType } from 'store/modules/auth/auth';
import { WithProjectProps } from 'components/Project/withProject';
import styled from 'styled-components/macro';

const { connect, selectors } = StoreProvider;

interface ProjectProps
  extends RouteComponentProps<{ id: string; codename: string }> {
  product: WithProjectProps['product'];
  project: WithProjectProps['project'];
  projectUUID: string;
  organization: OrganizationType;
  isProjectAccepted: WithProjectProps['isProjectAccepted'];
  isProjectArchived: WithProjectProps['isProjectArchived'];
}

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
    display: 'flex',
    overflow: 'auto',
    flexDirection: 'column',
    border: '1px solid #CECECA',
    boxShadow: ' 0 4px 12px 0 rgba(0,0,0,0.1)',
    borderRadius: '10px',
    cursor: 'pointer',
  },
  file: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    width: '95%',
  },
  aggregate: {
    margin: '0.2rem 0',
  },
  cancel: {
    backgroundColor: '#b00020',
    color: 'white',
    '&:hover': {
      backgroundColor: '#b00020b0',
    },
  },
  animContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: '4rem',
    '& rect': {
      fill: 'transparent',
    },
  },
}));

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 5rem;
`;

const Files = ({ projectUUID, organization }: ProjectProps) => {
  const classes = useStyles();
  const t = useFormatMessage();
  const [files, setFiles] = useState<{ name: string }[]>([]);
  const [runId, setRunId] = useState<number | null>(null);

  const extractIdFromData = (data: any) =>
    data.results && data.results[0] ? data.results[0].id : null;

  const fetchData = useCallback(() => {
    const orgName = organization.codename;

    projectUUID &&
      api.resources
        .getProjectTableResults(orgName, projectUUID)
        .then(({ data }) => {
          const tableId = extractIdFromData(data);

          tableId &&
            api.resources
              .getProjectTableRuns(orgName, tableId)
              .then(({ data }) => {
                const runId = extractIdFromData(data);

                runId &&
                  api.resources
                    .getProjectRunFiles(orgName, runId)
                    .then(({ data }) => {
                      if (data.files) {
                        setFiles(data.files);
                        setRunId(runId);
                      }
                    });
              });
        });
  }, [organization.codename, projectUUID]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onDownloadFileCsv = (name: string) => {
    api.resources
      .getFileCsv(organization.codename, runId as number, name)
      .then(({ data }) => {
        const url = window.URL.createObjectURL(new Blob([data]));

        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', name);
        document.body.appendChild(link);
        link.click();
      })
      .catch(() => {
        showToast({
          type: 'error',
          title: t(
            'errors.toast.general',
            'Something went wrong, please contact administrator'
          ),
        });
      });
  };

  return (
    <div style={{ marginTop: '2rem', marginLeft: '2rem', width: '100%' }}>
      {files.length ? (
        files.map((file: { name: string }) => (
          <Grid
            item
            xs={12}
            md={4}
            key={file.name}
            onClick={() => onDownloadFileCsv(file.name)}
          >
            <Paper className={classes.paper}>
              <span className={classes.file}>{file.name}</span>
            </Paper>
          </Grid>
        ))
      ) : (
        <Wrapper>
          <NoData
            title={t(
              'project.statistic.bexio.nodata.report.title',
              'Waiting for survey data'
            )}
            description={t(
              'project.statistic.nodata.files.description',
              'We will now automatically send the survey to your audience. As soon as the first responses are available, the files will be generated.'
            )}
          />
        </Wrapper>
      )}
    </div>
  );
};

export default compose(
  connect((state) => ({
    organization: selectors.authSelectors.userOrganization(state),
    projectUUID: selectors.projects.selected(state).uuid,
  }))
)(Files);
